import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import Layout from "../components/Layout.jsx";
import { kpiService } from "../services/apiService.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import * as XLSX from "xlsx";
import FYWeekPicker from "../components/FYWeekPicker"; // [EDITED: KPI Task - Mahesh Sharma]
import { exportKPIToPDF } from "../utils/kpiReportExport"; // [EDITED: KPI Task - Mahesh Sharma]
import { formatFYWeekLabel, formatFYWeekShort, getFYWeekOptions } from "../utils/fyWeekUtils"; // [EDITED: KPI Task - Mahesh Sharma]
import "./KpiReports.css";
import {
  Download,
  Calendar,
  BarChart2,
  FileSpreadsheet,
  TrendingUp,
  RefreshCw,
  AlertCircle,
  Search,
} from "lucide-react";

// ── helpers ────────────────────────────────────────────────────────────────────

// Raw API values (TL_per, hod_per, mg_per) are on 0–10 scale
// (TLPoints / totalPoints * 100, where totalPoints=100 and TLPoints is 0–10)
// Multiply by 10 to convert to a true 0–100 percentage.
const toPercent = (v) => {
  if (v === null || v === undefined || v === "" || v === "-") return null;
  const n = parseFloat(v);
  if (isNaN(n)) return null;
  return n * 10;
};

const getScoreColor = (pct) => {
  // New thresholds: below 90 -> poor (red), 90–95 -> good (yellow/orange), above 95 -> excellent (green)
  if (pct === null || pct === undefined) return "neutral";
  if (pct > 95) return "excellent";
  if (pct >= 90) return "good";
  return "poor";
};

const getScoreLabel = (pct) => {
  // Keep function for exports/legacy uses, but labels are no longer rendered in the UI.
  if (pct === null || pct === undefined) return "—";
  if (pct > 95) return "Green";
  if (pct >= 90) return "Yellow";
  return "Red";
};

const ScoreCell = ({ value }) => {
  const pct = toPercent(value);
  if (pct === null) {
    return <div className="kr-score kr-score--neutral"><span className="kr-score__value">—</span></div>;
  }
  const color = getScoreColor(pct);
  // UI should only show the colored percentage — no small label text
  return (
    <div className={`kr-score kr-score--${color}`}>
      <span className="kr-score__value">{pct.toFixed(1)}%</span>
    </div>
  );
};

const getCurrentYearMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const getWeekNumber = (date) => {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

const getPreviousWeek = () => {
  const today = new Date();
  const lastWeek = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - 7,
  );
  const year = lastWeek.getFullYear();
  const weekNum = getWeekNumber(lastWeek);
  return `${year}-W${weekNum.toString().padStart(2, "0")}`;
};

// ── component ──────────────────────────────────────────────────────────────────

export default function KpiReports() {
  const { user } = useAuth();

  const [tab, setTab] = useState("weekly"); // "weekly" | "monthly"

  // weekly state
  const [week, setWeek] = useState(getPreviousWeek());
  const [weekData, setWeekData] = useState([]);
  const [weekLoading, setWeekLoading] = useState(false);
  const [weekSearch, setWeekSearch] = useState("");

  // monthly state
  const [month, setMonth] = useState(getCurrentYearMonth());
  const [monthData, setMonthData] = useState([]);
  const [monthLoading, setMonthLoading] = useState(false);
  const [monthSearch, setMonthSearch] = useState("");

  // ── fetch weekly ─────────────────────────────────────────────────────────────

  const fetchWeekly = async (selectedWeek) => {
    if (!selectedWeek) return;
    setWeekLoading(true);
    setWeekData([]);
    try {
      // Send the full week string (e.g. "2026-W12") to the backend endpoint
      const res = await kpiService.getWeeklyKPI(selectedWeek);
      const data = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : [];
      setWeekData(data);
    } catch (err) {
      console.error("Weekly KPI fetch error:", err);
    } finally {
      setWeekLoading(false);
    }
  };

  const handleWeekChange = (e) => { // [EDITED: KPI Task - Mahesh Sharma]
    const val = typeof e === "string" ? e : (e?.target?.value || ""); // [EDITED: KPI Task - Mahesh Sharma]
    setWeek(val); // [EDITED: KPI Task - Mahesh Sharma]
    fetchWeekly(val); // [EDITED: KPI Task - Mahesh Sharma]
  }; // [EDITED: KPI Task - Mahesh Sharma]

  // ── fetch monthly ────────────────────────────────────────────────────────────

  const fetchMonthly = async (selectedMonth) => {
    if (!selectedMonth) return;
    setMonthLoading(true);
    setMonthData([]);
    try {
      const res = await kpiService.getMonthlyKPI(selectedMonth);
      const data = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : [];
      setMonthData(data);
    } catch (err) {
      console.error("Monthly KPI fetch error:", err);
    } finally {
      setMonthLoading(false);
    }
  };

  const handleMonthChange = (e) => {
    const val = e.target.value;
    setMonth(val);
    fetchMonthly(val);
  };

  // ── auto-load previous week on mount ──────────────────────────────────────

  useEffect(() => {
    fetchWeekly(week);
  }, []);

  // Auto-load monthly report when user switches to the monthly tab
  useEffect(() => {
    if (tab === "monthly" && month) {
      fetchMonthly(month);
    }
  }, [tab]);

  // ── download helpers ─────────────────────────────────────────────────────────

  const downloadWeeklyExcel = () => {
    if (!weekData.length) return;
    const rows = filteredWeekData.map((r, i) => ({
      "#": i + 1,
      Week: formatFYWeekLabel(week),
      "Employee ID": r.employee_code || "",
      "Employee Name": r.employee_name || "",
      Department: r.dept_name || "",
      "Submission Date": r.submission_date
        ? r.submission_date.split("T")[0]
        : "",
      "TL Score (%)": toPercent(r.TL_per) !== null ? toPercent(r.TL_per).toFixed(1) : "",
      "TL Rating": getScoreLabel(toPercent(r.TL_per)),
      "HOD Score (%)": toPercent(r.hod_per) !== null ? toPercent(r.hod_per).toFixed(1) : "",
      "HOD Rating": getScoreLabel(toPercent(r.hod_per)),
      "Management Score (%)": toPercent(r.mg_per) !== null ? toPercent(r.mg_per).toFixed(1) : "",
      "Management Rating": getScoreLabel(toPercent(r.mg_per)),
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 4 }, { wch: 40 }, { wch: 12 }, { wch: 22 }, { wch: 16 }, { wch: 15 },
      { wch: 12 }, { wch: 14 }, { wch: 12 }, { wch: 14 },
      { wch: 16 }, { wch: 18 },
    ];
    XLSX.utils.book_append_sheet(wb, ws, formatFYWeekShort(week) || `Weekly_KPI_${week}`);
    XLSX.writeFile(wb, `KPI_Weekly_${(formatFYWeekShort(week) || week).replace(/\s+/g, "_")}.xlsx`);
  };

  // [EDITED: KPI Task - Mahesh Sharma]
  const downloadWeeklyPDF = () => { // [EDITED: KPI Task - Mahesh Sharma]
    if (!filteredWeekData.length) return; // [EDITED: KPI Task - Mahesh Sharma]
    exportKPIToPDF(filteredWeekData, week, "Weekly KPI Performance Report"); // [EDITED: KPI Task - Mahesh Sharma]
  }; // [EDITED: KPI Task - Mahesh Sharma]

  const downloadMonthlyExcel = () => {
    if (!monthData.length) return;
    const rows = filteredMonthData.map((r, i) => {
      const base = {
        "#": i + 1,
        "Employee ID": r.employee_code || r.EmployeeCode || "",
        "Employee Name": r.employee_name || r.EmployeeName || "",
        Department: r.dept_name || r.Department || "",
      };
      // include all other fields from backend
      Object.keys(r).forEach((k) => {
        if (!["employee_code","EmployeeCode","employee_name","EmployeeName","dept_name","Department"].includes(k)) {
          base[k] = r[k];
        }
      });
      return base;
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, `Monthly_KPI_${month}`);
    XLSX.writeFile(wb, `KPI_Monthly_${month}.xlsx`);
  };

  // [EDITED: KPI Task - Mahesh Sharma]
  const downloadMonthlyPDF = () => { // [EDITED: KPI Task - Mahesh Sharma]
    if (!filteredMonthData.length) return; // [EDITED: KPI Task - Mahesh Sharma]
    exportKPIToPDF(filteredMonthData, month, "Monthly KPI Performance Report"); // [EDITED: KPI Task - Mahesh Sharma]
  }; // [EDITED: KPI Task - Mahesh Sharma]

  // ── filter / search ──────────────────────────────────────────────────────────

  const filteredWeekData = weekData.filter((r) => {
    if (!weekSearch.trim()) return true;
    const q = weekSearch.toLowerCase();
    return (
      (r.employee_name || "").toLowerCase().includes(q) ||
      (r.employee_code || "").toLowerCase().includes(q) ||
      (r.dept_name || "").toLowerCase().includes(q)
    );
  });

  const filteredMonthData = monthData.filter((r) => {
    if (!monthSearch.trim()) return true;
    const q = monthSearch.toLowerCase();
    const name = (r.employee_name || r.EmployeeName || "").toLowerCase();
    const code = (r.employee_code || r.EmployeeCode || "").toLowerCase();
    const dept = (r.dept_name || r.Department || "").toLowerCase();
    return name.includes(q) || code.includes(q) || dept.includes(q);
  });

  // ── access guard (after all hooks) ─────────────────────────────────────────
  if (user && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout>
      <div className="page-header">
        <div className="row align-items-center">
          <div className="col-md-6">
            <div className="pagetitle d-flex align-items-center gap-2">
              <div className="kr-header__icon">
                <BarChart2 size={20} />
              </div>
              <div>
                <h1 className="kr-header__title">KPI Reports</h1>
                <p className="kr-header__sub">Download weekly &amp; monthly KPI performance reports ({formatFYWeekLabel(week)})</p>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="d-flex justify-content-end gap-2">
              {tab === "weekly" && (
                <>
                  <button
                    className="kr-btn kr-btn--primary"
                    onClick={downloadWeeklyExcel}
                    disabled={!filteredWeekData.length}
                    title="Download Excel"
                    aria-label="Download Excel"
                    style={{ padding: '0.4rem', width: '32px', justifyContent: 'center' }}
                  >
                    <FileSpreadsheet size={15} />
                  </button>
                  <button
                    className="kr-btn kr-btn--danger"
                    onClick={downloadWeeklyPDF}
                    disabled={!filteredWeekData.length}
                    title="Download PDF"
                    aria-label="Download PDF"
                    style={{ backgroundColor: '#dc2626', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.4rem', width: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <Download size={15} />
                  </button>
                </>
              )}
              {tab === "monthly" && (
                <>
                  <button
                    className="kr-btn kr-btn--green"
                    onClick={downloadMonthlyExcel}
                    disabled={!filteredMonthData.length}
                    title="Download Excel"
                    aria-label="Download Excel"
                    style={{ padding: '0.4rem', width: '32px', justifyContent: 'center' }}
                  >
                    <Download size={15} />
                  </button>
                  <button
                    className="kr-btn kr-btn--danger"
                    onClick={downloadMonthlyPDF}
                    disabled={!filteredMonthData.length}
                    title="Download PDF"
                    aria-label="Download PDF"
                    style={{ backgroundColor: '#dc2626', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.4rem', width: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <Download size={15} />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <section className="section">
        {/* TABS */}
        <div className="kr-tabs">
          <button
            className={`kr-tab ${tab === "weekly" ? "kr-tab--active" : ""}`}
            onClick={() => setTab("weekly")}
          >
            <Calendar size={15} />
            Weekly Report
          </button>
          <button
            className={`kr-tab ${tab === "monthly" ? "kr-tab--active" : ""}`}
            onClick={() => setTab("monthly")}
          >
            <TrendingUp size={15} />
            Monthly Report
          </button>
        </div>

        {/* ── WEEKLY TAB ─────────────────────────────────────────────────────── */}
        {tab === "weekly" && (
          <div className="kr-panel">
            {/* Controls */}
            <div className="kr-controls">
              <div className="kr-controls__group">
                <label className="kr-controls__label">
                  <Calendar size={14} /> Select Week
                </label>
                <FYWeekPicker
                  value={week}
                  onChange={handleWeekChange}
                  style={{ minWidth: '320px' }}
                />
              </div>
              <div className="kr-controls__group kr-controls__group--search">
                <label className="kr-controls__label">
                  <Search size={14} /> Search
                </label>
                <div className="kr-search">
                  <Search size={14} className="kr-search__icon" />
                  <input
                    type="text"
                    className="kr-input kr-input--search"
                    placeholder="Name, ID or department…"
                    value={weekSearch}
                    onChange={(e) => setWeekSearch(e.target.value)}
                  />
                </div>
              </div>
              {weekData.length > 0 && (
                <span className="kr-count-badge">{filteredWeekData.length} records</span>
              )}
            </div>

            {/* Legend */}
            <div className="kr-legend">
              <span className="kr-legend__item kr-legend__item--excellent"><span></span> &gt; 95% (Green)</span>
              <span className="kr-legend__item kr-legend__item--good"><span></span> 90–95% (Yellow)</span>
              <span className="kr-legend__item kr-legend__item--poor"><span></span> &lt; 90% (Red)</span>
            </div>

            {/* Table */}
            <div className="kr-table-wrap">
              <table className="kr-table">
                <thead>
                  <tr>
                    <th className="kr-th kr-th--sno">#</th>
                    <th className="kr-th">Employee ID</th>
                    <th className="kr-th">Employee Name</th>
                    <th className="kr-th">Department</th>
                    <th className="kr-th">Submission Date</th>
                    <th className="kr-th kr-th--score">TL Score</th>
                    <th className="kr-th kr-th--score">HOD Score</th>
                    <th className="kr-th kr-th--score">Mgmt Score</th>
                  </tr>
                </thead>
                <tbody>
                  {weekLoading ? (
                    <tr>
                      <td colSpan="8" className="kr-state-cell">
                        <div className="kr-spinner"></div>
                        <span className="kr-state-text">Loading weekly KPI data…</span>
                      </td>
                    </tr>
                  ) : !week ? (
                    <tr>
                      <td colSpan="8" className="kr-state-cell">
                        <Calendar size={36} className="kr-state-icon" />
                        <span className="kr-state-text">Select a week to load the report</span>
                      </td>
                    </tr>
                  ) : filteredWeekData.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="kr-state-cell">
                        <AlertCircle size={36} className="kr-state-icon" />
                        <span className="kr-state-text">No data found for the selected week</span>
                      </td>
                    </tr>
                  ) : (
                    filteredWeekData.map((row, i) => {
                      return (
                        <tr key={i} className={`kr-row ${i % 2 === 0 ? "kr-row--even" : ""}`}>
                          <td className="kr-td kr-td--sno">{i + 1}</td>
                          <td className="kr-td">
                            <span className="kr-emp-code">{row.employee_code}</span>
                          </td>
                          <td className="kr-td kr-td--name">{row.employee_name}</td>
                          <td className="kr-td">
                            <span className="kr-dept-badge">{row.dept_name}</span>
                          </td>
                          <td className="kr-td kr-td--date">
                            {row.submission_date ? row.submission_date.split("T")[0] : "—"}
                          </td>
                          <td className="kr-td kr-td--score">
                            <ScoreCell value={row.TL_per} />
                          </td>
                          <td className="kr-td kr-td--score">
                            <ScoreCell value={row.hod_per} />
                          </td>
                          <td className="kr-td kr-td--score">
                            <ScoreCell value={row.mg_per} />
                          </td>
                          
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {filteredWeekData.length > 0 && (
              <div className="kr-footer">Showing <strong>{filteredWeekData.length}</strong> of <strong>{weekData.length}</strong> records</div>
            )}
          </div>
        )}

        {/* ── MONTHLY TAB ───────────────────────────────────────────────────── */}
        {tab === "monthly" && (
          <div className="kr-panel">
            {/* Controls */}
            <div className="kr-controls">
              <div className="kr-controls__group">
                <label className="kr-controls__label">
                  <Calendar size={14} /> Select Month
                </label>
                <input
                  type="month"
                  className="kr-input"
                  value={month}
                  onChange={handleMonthChange}
                />
              </div>
              <div className="kr-controls__group kr-controls__group--search">
                <label className="kr-controls__label">
                  <Search size={14} /> Search
                </label>
                <div className="kr-search">
                  <Search size={14} className="kr-search__icon" />
                  <input
                    type="text"
                    className="kr-input kr-input--search"
                    placeholder="Name, ID or department…"
                    value={monthSearch}
                    onChange={(e) => setMonthSearch(e.target.value)}
                  />
                </div>
              </div>
              <button
                className="kr-btn kr-btn--primary"
                onClick={() => fetchMonthly(month)}
                disabled={monthLoading || !month}
                title="Refresh"
              >
                {monthLoading ? (
                  <span className="kr-spinner kr-spinner--sm"></span>
                ) : (
                  <RefreshCw size={14} />
                )}
              </button>
              {monthData.length > 0 && (
                <span className="kr-count-badge">{filteredMonthData.length} records</span>
              )}
            </div>

            {/* Table */}
            <div className="kr-table-wrap">
              <table className="kr-table">
                <thead>
                    <tr>
                      <th className="kr-th kr-th--sno">#</th>
                      <th className="kr-th">Employee ID</th>
                      <th className="kr-th">Employee Name</th>
                      <th className="kr-th">Department</th>
                      <th className="kr-th">Submission Date</th>
                      <th className="kr-th kr-th--score">TL Score</th>
                      <th className="kr-th kr-th--score">HOD Score</th>
                      <th className="kr-th kr-th--score">Mgmt Score</th>
                    </tr>
                </thead>
                <tbody>
                  {monthLoading ? (
                    <tr>
                      <td colSpan="8" className="kr-state-cell">
                        <div className="kr-spinner"></div>
                        <span className="kr-state-text">Loading monthly KPI data…</span>
                      </td>
                    </tr>
                  ) : filteredMonthData.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="kr-state-cell">
                        <Calendar size={36} className="kr-state-icon" />
                        <span className="kr-state-text">
                          {monthData.length === 0 && month
                            ? "No data found for the selected month"
                            : "Select a month to view the report"}
                        </span>
                      </td>
                    </tr>
                  ) : (
                    filteredMonthData.map((row, i) => {
                      const tl = row.TL_per ?? row.tl_per ?? row.TLScore ?? null;
                      const hod = row.hod_per ?? row.HODScore ?? null;
                      const mg = row.mg_per ?? row.mgScore ?? row.MgScore ?? null;
                      const name = row.employee_name || row.EmployeeName || "";
                      const code = row.employee_code || row.EmployeeCode || "";
                      const dept = row.dept_name || row.Department || "";
                      const subDate = row.submission_date || row.SubmissionDate || "";
                      return (
                        <tr key={i} className={`kr-row ${i % 2 === 0 ? "kr-row--even" : ""}`}>
                          <td className="kr-td kr-td--sno">{i + 1}</td>
                          <td className="kr-td">
                            <span className="kr-emp-code">{code}</span>
                          </td>
                          <td className="kr-td kr-td--name">{name}</td>
                          <td className="kr-td">
                            <span className="kr-dept-badge">{dept}</span>
                          </td>
                          <td className="kr-td kr-td--date">
                            {subDate ? subDate.split("T")[0] : "—"}
                          </td>
                          <td className="kr-td kr-td--score"><ScoreCell value={tl} /></td>
                          <td className="kr-td kr-td--score"><ScoreCell value={hod} /></td>
                          <td className="kr-td kr-td--score"><ScoreCell value={mg} /></td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {filteredMonthData.length > 0 && (
              <div className="kr-footer">Showing <strong>{filteredMonthData.length}</strong> of <strong>{monthData.length}</strong> records</div>
            )}
          </div>
        )}
      </section>
    </Layout>
  );
}
